import { moduleActionContext } from '.';
import { AutoFactory, OnlinePortalPersonaApi, PersonaSettingsInternal, HasInProgressInquiryRequest, HasInProgressInquiryResult } from '@sageworks/jpi';
import { getOnlinePortalSubdomain } from '@/utils/UrlUtils';
import { defineModule } from 'direct-vuex';
import { UrlUtils } from '@/utils';
import { PersonaGenerateTokenInternal } from '@sageworks/jpi/src/auto-generated/model/personaGenerateTokenInternal';

const actionContext = (context: any) => moduleActionContext(context, PersonaModule);

export interface PersonaModuleState {
	settings: PersonaSettingsInternal | null;
	hasPendingInquiry: boolean | null;
}
const PersonaModule = defineModule({
	namespaced: true,
	state: () => {
		return {
			settings: null,
			hasPendingInquiry: null
		} as PersonaModuleState;
	},
	mutations: {
		SET_PERSONA_SETTINGS(state, settings: PersonaSettingsInternal) {
			state.settings = settings;
		},
		SET_HAS_PENDING_INQUIRY(state, hasPendingInquiry: boolean) {
			state.hasPendingInquiry = hasPendingInquiry;
		}
	},
	actions: {
		async fetchPersonaSettings(context): Promise<void> {
			const { commit } = actionContext(context);
			const personaService = AutoFactory.get(OnlinePortalPersonaApi);
			const currentSubdomain = getOnlinePortalSubdomain();
			var keys = await personaService.getPersonaSettingsBySubdomain(currentSubdomain);
			commit.SET_PERSONA_SETTINGS(keys);
		},
		async checkPendingInquiry(context): Promise<void> {
			const { commit, rootState } = actionContext(context);
			const username = rootState.Login.userEmail;
			const secret = rootState.Login.userPassword;
			const subdomain = getOnlinePortalSubdomain();

			try {
				const personaService = AutoFactory.get(OnlinePortalPersonaApi);
				const request: HasInProgressInquiryRequest = {
					username: username ?? '',
					secret: secret ?? ''
				};
				const result = (await personaService.hasInProgressInquiry(subdomain, request)) as HasInProgressInquiryResult;
				commit.SET_HAS_PENDING_INQUIRY(result.hasInProgressInquiry ?? false);
			} catch (error) {
				// If the API call fails, assume no pending inquiry
				commit.SET_HAS_PENDING_INQUIRY(false);
			}
		},
		async generatePersonaIdentityToken(context, payload: { inquiryId: string; rememberThisDevice: boolean }): Promise<any> {
			const { rootState, rootCommit } = actionContext(context);
			const subdomain = UrlUtils.getOnlinePortalSubdomain();
			const username = rootState.Login.userEmail;
			const secret = rootState.Login.userPassword;

			try {
				const tokenResult = await AutoFactory.get(OnlinePortalPersonaApi).generateVerificationToken({
					subDomain: subdomain,
					username: username,
					secret: secret,
					inquiryId: payload.inquiryId
				} as PersonaGenerateTokenInternal);

				if (!tokenResult.identityVerified || !tokenResult.deviceToken) {
					// Return the full result so the component can check isInProgress
					return tokenResult;
				}
				rootCommit.Authentication.SET_DEVICE_TOKEN({ deviceToken: tokenResult.deviceToken, rememberThisDevice: payload.rememberThisDevice });
				return tokenResult;
			} catch (error) {
				throw new Error('Verification failed');
			}
		},
		async saveFailedPersonaVerification(context, payload: { inquiryId: string }): Promise<void> {
			const { rootState } = actionContext(context);
			const subdomain = UrlUtils.getOnlinePortalSubdomain();
			const username = rootState.Login.userEmail;
			const secret = rootState.Login.userPassword;
			const request = {
				subDomain: subdomain,
				username: username,
				secret: secret,
				inquiryId: payload.inquiryId
			} as PersonaGenerateTokenInternal;

			try {
				await AutoFactory.get(OnlinePortalPersonaApi).saveFailedVerification(request);
			} catch (error) {
				throw new Error('Failed to save verification');
			}
		}
	}
});

export default PersonaModule;
